import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react';
import { CompactLogo } from './AnimatedLogo';
import { useSettings } from '../hooks/useSettings';
import { getGoogleMapsUrl, getTikTokUrl, TikTokIcon, isUsefulSocialUrl } from '../lib/social-links';


export function Footer() {
  const { settings, loading: settingsLoading } = useSettings();

  const phone = settings?.contact_phone?.trim();
  const email = settings?.contact_email?.trim();
  const address = settings?.contact_address?.trim();
  const workingHours = settings?.contact_working_hours?.trim();
  const googleMapsUrl = getGoogleMapsUrl(settings);
  const tiktokUrl = getTikTokUrl(settings);

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* About */}
          <div>
            <div className="mb-4">
              <CompactLogo variant="light" />
            </div>
            <p className="text-sm mb-4">
              Votre partenaire d'exception pour la production audiovisuelle et événementielle depuis 1988.
            </p>
            <div className="flex gap-3">
              {settings?.social_facebook && isUsefulSocialUrl(settings.social_facebook) && (
                <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gradient-fire transition-all">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {settings?.social_instagram && isUsefulSocialUrl(settings.social_instagram) && (
                <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gradient-fire transition-all">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {settings?.social_youtube && isUsefulSocialUrl(settings.social_youtube) && (
                <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gradient-fire transition-all">
                  <Youtube className="w-5 h-5" />
                </a>
              )}
              {settings?.social_linkedin && isUsefulSocialUrl(settings.social_linkedin) && (
                <a href={settings.social_linkedin} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gradient-fire transition-all">
                  <Linkedin className="w-5 h-5" />
                </a>
              )}
              {tiktokUrl && (
                <a href={tiktokUrl} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gradient-fire transition-all" aria-label="TikTok">
                  <TikTokIcon className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">Nos Services</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <div className="text-gray-500 flex items-center justify-between gap-2 cursor-not-allowed group">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-700 rounded-full"></span>
                    Mariage Photo & Vidéo
                  </div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded-full">Bientôt</span>
                </div>
              </li>
              <li>
                <div className="text-gray-500 flex items-center justify-between gap-2 cursor-not-allowed group">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-700 rounded-full"></span>
                    Location Photobooth
                  </div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded-full">Bientôt</span>
                </div>
              </li>
              <li>
                <div className="text-gray-500 flex items-center justify-between gap-2 cursor-not-allowed group">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-700 rounded-full"></span>
                    Studio de Création
                  </div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded-full">Bientôt</span>
                </div>
              </li>
              <li>
                <div className="text-gray-500 flex items-center justify-between gap-2 cursor-not-allowed group">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-gray-700 rounded-full"></span>
                    Tous les services
                  </div>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-gray-800 text-gray-400 rounded-full">Bientôt</span>
                </div>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">Portfolio</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/portfolio/mariage" className="hover:text-primary-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  Mariage
                </Link>
              </li>
              <li>
                <Link to="/portfolio/photobooth" className="hover:text-primary-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  Photobooth
                </Link>
              </li>
              <li>
                <Link to="/portfolio/corporate" className="hover:text-primary-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  Corporate
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="hover:text-primary-400 transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                  Galerie complète
                </Link>
              </li>
            </ul>
          </div>



          {/* Informations Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">Informations</h3>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="hover:text-primary-400 transition-colors flex items-center gap-2">À propos</Link></li>
              <li><Link to="/faq" className="hover:text-primary-400 transition-colors flex items-center gap-2">FAQ</Link></li>
              <li><Link to="/blog" className="hover:text-primary-400 transition-colors flex items-center gap-2">Blog</Link></li>
              <li><Link to="/contact" className="hover:text-primary-400 transition-colors flex items-center gap-2">Contact</Link></li>
              <li><Link to="/cgv" className="hover:text-primary-400 transition-colors flex items-center gap-2">CGV</Link></li>
              <li><Link to="/privacy" className="hover:text-primary-400 transition-colors flex items-center gap-2">Politique de confidentialité</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-lg">Contactez-nous</h3>
            {settingsLoading ? (
              <ul className="space-y-3 text-sm">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="h-10 bg-gray-800 rounded-lg animate-pulse" />
                ))}
              </ul>
            ) : (
              <ul className="space-y-3 text-sm">
                {phone && (
                  <li className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-primary-400 transition-colors">
                        {phone}
                      </a>
                      {workingHours && (
                        <p className="text-xs text-gray-500">{workingHours}</p>
                      )}
                    </div>
                  </li>
                )}
                {email && (
                  <li className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <a href={`mailto:${email}`} className="hover:text-primary-400 transition-colors">
                      {email}
                    </a>
                  </li>
                )}
                {address && (
                  <li className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
                    {googleMapsUrl ? (
                      <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors">
                        {address}
                      </a>
                    ) : (
                      <p>{address}</p>
                    )}
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
            <p>&copy; {new Date().getFullYear()} Lbenna Prod. Tous droits réservés.</p>
            <ul className="flex gap-6">
              <li><Link to="/portfolio" className="hover:text-primary-400 transition-colors">Galerie</Link></li>
              <li><Link to="/about" className="hover:text-primary-400 transition-colors">À Propos</Link></li>
              <li><Link to="/faq" className="hover:text-primary-400 transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-primary-400 transition-colors">Contact</Link></li>
              <li><Link to="/login" className="hover:text-primary-400 transition-colors">Espace Client</Link></li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
